import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import Select from 'react-select';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import {
  FileText,
  DollarSign,
  Calendar,
  Hash,
  Edit2,
  Trash2,
  Plus,
  CheckCircle,
  AlertCircle,
  Briefcase,
  List,
  X,
  Search
} from 'lucide-react';

/**
 * Modern verification modal with three modes:
 * 1. Template: Use predefined templates with editable preview
 * 2. Manual: Create verification from scratch
 */
const VerificationModal = ({
  show,
  onHide,
  companyId,
  invoice,
  invoiceId,
  onConfirm,
  isLoading,
  error,
  setError
}) => {
  // Mode state
  const [mode, setMode] = useState('template'); // 'template' or 'manual'

  // Common state
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');

  // Template mode state
  const [verificationTemplates, setVerificationTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedTemplateDetails, setSelectedTemplateDetails] = useState(null);
  const [templateParameters, setTemplateParameters] = useState([]);
  const [parameterValues, setParameterValues] = useState({});
  const [showPreview, setShowPreview] = useState(false);

  // Transaction state (shared between modes)
  const [transactions, setTransactions] = useState([]);
  const [totalDebit, setTotalDebit] = useState(0);
  const [totalCredit, setTotalCredit] = useState(0);

  // Fetch data when modal is shown
  useEffect(() => {
    if (show) {
      fetchPaymentAccounts();
      fetchAllAccounts();
      fetchVerificationTemplates();
      resetState();
    }
  }, [show]);

  // Calculate totals when transactions change
  useEffect(() => {
    calculateTotals();
  }, [transactions]);

  // Clear and initialize transactions when switching modes
  useEffect(() => {
    if (mode === 'manual') {
      // Switching to manual mode - clear any template transactions and initialize
      if (allAccounts.length > 0) {
        initializeManualMode();
      } else {
        // Accounts not loaded yet, clear transactions for now
        setTransactions([]);
      }
    } else if (mode === 'template') {
      // Switching to template mode - clear any manual transactions
      setTransactions([]);
      setShowPreview(false);
    }
  }, [mode]);

  // Set default selected account when accounts are loaded
  useEffect(() => {
    if (allAccounts.length > 0 && !selectedAccount) {
      const account1930 = allAccounts.find(a => a.account_number === '1930' || a.account_id === 1930);
      const firstBankAccount = allAccounts.find(a => {
        const accNum = parseInt(a.account_number);
        return accNum >= 1900 && accNum < 2000;
      });
      const defaultAcct = account1930 || firstBankAccount;
      if (defaultAcct) {
        setSelectedAccount(defaultAcct.account_id);
      }
    }
  }, [allAccounts]);

  // Initialize manual mode transactions when accounts load
  useEffect(() => {
    if (mode === 'manual' && allAccounts.length > 0 && transactions.length === 0) {
      initializeManualMode();
    }
  }, [allAccounts, mode]);

  const resetState = () => {
    setMode('template');
    setSelectedAccount(''); // Will be set from fetched accounts
    setSelectedTemplate('');
    setSelectedTemplateDetails(null);
    setTemplateParameters([]);
    setParameterValues({});
    setShowPreview(false);
    setTransactions([]);
  };

  // Initialize manual mode with default transactions
  const initializeManualMode = () => {
    // Try to find account 1930, or fall back to first bank account (1900-1999 range)
    let defaultAccount = allAccounts.find(a => a.account_number === '1930' || a.account_id === 1930);

    if (!defaultAccount) {
      // Find first bank account in the 1900-1999 range
      defaultAccount = allAccounts.find(a => {
        const accNum = parseInt(a.account_number);
        return accNum >= 1900 && accNum < 2000;
      });
    }

    const invoiceAmount = invoice?.amount || 0;

    setTransactions([
      {
        account_id: defaultAccount ? defaultAccount.account_id : '',
        account_number: defaultAccount ? defaultAccount.account_number : '',
        account_name: defaultAccount ? defaultAccount.account_name : '',
        debit: '',
        credit: invoiceAmount
      },
      {
        account_id: '',
        account_number: '',
        account_name: '',
        debit: '',
        credit: ''
      }
    ]);
  };

  // Fetch payment accounts
  const fetchPaymentAccounts = async () => {
    // Payment accounts will be filtered from all accounts (1900-1999 range)
    try {
      const accountsApiUrl = getApiUrl(`${companyId}/accounting/accounts`);
      const response = await axios.get(accountsApiUrl, { withCredentials: true });
      const accounts = response.data.accounts || response.data || [];

      // Filter for bank accounts (typically 1900-1999 range)
      const bankAccounts = accounts.filter(acc => {
        const accNum = parseInt(acc.account_number);
        return accNum >= 1900 && accNum < 2000;
      });

      setPaymentAccounts(bankAccounts);
    } catch (error) {
      console.error('Error fetching payment accounts:', error);
      setError('Kunde inte hämta betalningskonton.');
    }
  };

  // Fetch all accounts for manual mode
  const fetchAllAccounts = async () => {
    try {
      const accountsApiUrl = getApiUrl(`${companyId}/accounting/accounts`);
      const response = await axios.get(accountsApiUrl, { withCredentials: true });
      const accounts = response.data.accounts || response.data || [];

      // Map to ensure consistent structure
      const mappedAccounts = accounts.map(acc => ({
        account_id: acc.account_id || acc.account_number,
        account_number: acc.account_number,
        account_name: acc.account_name || acc.name || ''
      }));

      setAllAccounts(mappedAccounts);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setError('Kunde inte hämta kontoplanen.');
    }
  };

  // Fetch verification templates
  const fetchVerificationTemplates = async () => {
    try {
      const getVerificationTemplatesApiUrl = getApiUrl(`${companyId}/templates`);
      const response = await axios.get(getVerificationTemplatesApiUrl, {
        withCredentials: true,
      });
      setVerificationTemplates(response.data.templates);
    } catch (error) {
      console.error('Error fetching verification templates:', error);
      setError('Kunde inte hämta verifikationsmallar.');
    }
  };

  // Handle template change
  const handleTemplateChange = async (selectedOption) => {
    if (!selectedOption) {
      // Clear selection
      setSelectedTemplate('');
      setSelectedTemplateDetails(null);
      setTemplateParameters([]);
      setParameterValues({});
      setTransactions([]);
      setShowPreview(false);
      return;
    }

    const selectedTemplateId = selectedOption.value;
    setSelectedTemplate(selectedTemplateId);
    setTemplateParameters([]);
    setParameterValues({});
    setTransactions([]);
    setShowPreview(false);

    try {
      const templateDetailsUrl = getApiUrl(`${companyId}/templates/${selectedTemplateId}`);
      const response = await axios.get(templateDetailsUrl, { withCredentials: true });
      const template = response.data.template;
      setSelectedTemplateDetails(template);
      setTemplateParameters(template.parameters || []);
    } catch (error) {
      setError('Kunde inte hämta malldetaljer.');
    }
  };

  // Clear template selection
  const handleClearTemplate = () => {
    setSelectedTemplate('');
    setSelectedTemplateDetails(null);
    setTemplateParameters([]);
    setParameterValues({});
    setTransactions([]);
    setShowPreview(false);
  };

  // Generate preview from template
  const handleGeneratePreview = async () => {
    setError(null);
    setTransactions([]);
    setShowPreview(false);

    try {
      const parameters = { ...parameterValues };
      const userSelectedAccount = selectedAccount;

      const suggestionApiUrl = getApiUrl(`${companyId}/templates/${selectedTemplate}/suggestion`);
      const response = await axios.post(
        suggestionApiUrl,
        { parameters, userSelectedAccount },
        { withCredentials: true }
      );

      const suggestedTransactions = response.data.suggestedTransactions;
      setTransactions(suggestedTransactions);
      setShowPreview(true);
    } catch (error) {
      setError('Kunde inte generera förhandsgranskning.');
    }
  };

  // Add new transaction row (manual mode)
  const handleAddTransaction = () => {
    // For the first transaction in manual mode, try to find account 1930 or first bank account
    const isFirstTransaction = transactions.length === 0;
    let defaultAccount = null;

    if (isFirstTransaction) {
      defaultAccount = allAccounts.find(a => a.account_number === '1930' || a.account_id === 1930);
      if (!defaultAccount) {
        // Find first bank account in the 1900-1999 range
        defaultAccount = allAccounts.find(a => {
          const accNum = parseInt(a.account_number);
          return accNum >= 1900 && accNum < 2000;
        });
      }
    }

    setTransactions([
      ...transactions,
      {
        account_id: isFirstTransaction && defaultAccount ? defaultAccount.account_id : '',
        account_number: isFirstTransaction && defaultAccount ? defaultAccount.account_number : '',
        account_name: isFirstTransaction && defaultAccount ? defaultAccount.account_name : '',
        debit: '',
        credit: ''
      }
    ]);
  };

  // Remove transaction row
  const handleRemoveTransaction = (index) => {
    // In manual mode, always keep at least 2 rows
    if (mode === 'manual' && transactions.length <= 2) {
      return;
    }
    setTransactions(transactions.filter((_, i) => i !== index));
  };

  // Update transaction field
  const handleTransactionChange = (index, field, value) => {
    const updatedTransactions = [...transactions];

    if (field === 'account_id') {
      const account = allAccounts.find(a => a.account_id === parseInt(value));
      if (account) {
        updatedTransactions[index].account_id = account.account_id;
        updatedTransactions[index].account_number = account.account_number;
        updatedTransactions[index].account_name = account.account_name;
      }
    } else {
      updatedTransactions[index][field] = value;
    }

    setTransactions(updatedTransactions);
  };

  // Handle account selection change from react-select
  const handleAccountSelectChange = (index, selectedOption) => {
    const updatedTransactions = [...transactions];

    if (selectedOption) {
      updatedTransactions[index].account_id = selectedOption.value;
      updatedTransactions[index].account_number = selectedOption.account_number;
      updatedTransactions[index].account_name = selectedOption.account_name;
    } else {
      // Clear selection
      updatedTransactions[index].account_id = '';
      updatedTransactions[index].account_number = '';
      updatedTransactions[index].account_name = '';
    }

    setTransactions(updatedTransactions);
  };

  // Calculate totals
  const calculateTotals = () => {
    let debit = 0;
    let credit = 0;

    transactions.forEach((txn) => {
      debit += parseFloat(txn.debit || 0);
      credit += parseFloat(txn.credit || 0);
    });

    setTotalDebit(debit);
    setTotalCredit(credit);
  };

  // Check if verification is valid
  const isVerificationValid = () => {
    if (transactions.length === 0) return false;
    if (Math.abs(totalDebit - totalCredit) > 0.01) return false; // Allow small rounding differences

    // Check all transactions have required fields
    return transactions.every(txn =>
      txn.account_id &&
      (parseFloat(txn.debit || 0) > 0 || parseFloat(txn.credit || 0) > 0)
    );
  };

  // Handle confirm
  const handleConfirmVerification = () => {
    onConfirm({
      selectedAccount,
      selectedTemplate: mode === 'template' ? selectedTemplate : null,
      transactions,
      parameterValues: mode === 'template' ? parameterValues : null
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  return (
    <>
      <style>{`
        .type-selector {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .type-option {
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          padding: 1.5rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s ease;
          background: white;
        }

        .type-option:hover {
          border-color: #cbd5e1;
          background: #f8fafc;
        }

        .type-option.selected {
          border-color: #44814E;
          background: #eff7f1ff;
        }

        .type-icon {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 0.75rem;
          background: #f1f5f9;
          color: #64748b;
          transition: all 0.2s ease;
        }

        .type-option.selected .type-icon {
          background: #44814E;
          color: white;
        }

        .type-label {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.25rem;
          font-size: 1rem;
        }

        .type-description {
          font-size: 0.875rem;
          color: #64748b;
        }

        .close-button {
          position: absolute;
          top: 1.25rem;
          right: 1.5rem;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          color: #64748b;
          width: 32px;
          height: 32px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .close-button:hover {
          background: #e2e8f0;
          color: #475569;
        }
      `}</style>

      <Modal show={show} onHide={onHide} size="lg">
        <Modal.Header style={styles.header}>
          <button type="button" className="close-button" onClick={onHide}>
            <X size={16} />
          </button>
          <Modal.Title style={styles.title}>
            <FileText size={24} style={{ marginRight: '12px', color: '#3b82f6' }} />
            Skapa verifikation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={styles.body}>
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible style={styles.alert}>
              <AlertCircle size={18} style={{ marginRight: '8px' }} />
              {error}
            </Alert>
          )}

          {/* Mode Selection Cards */}
          <div style={styles.section}>
            <h5 style={styles.sectionTitle}>
              <Briefcase size={20} />
              Välj metod
            </h5>

            <div className="type-selector">
              <div
                className={`type-option ${mode === 'template' ? 'selected' : ''}`}
                onClick={() => setMode('template')}
              >
                <div className="type-icon">
                  <List size={24} />
                </div>
                <div className="type-label">Mall</div>
                <div className="type-description">Använd färdiga mallar med parametrar</div>
              </div>

              <div
                className={`type-option ${mode === 'manual' ? 'selected' : ''}`}
                onClick={() => setMode('manual')}
              >
                <div className="type-icon">
                  <Edit2 size={24} />
                </div>
                <div className="type-label">Manuell</div>
                <div className="type-description">Skapa verifikation från grunden</div>
              </div>
            </div>
          </div>

          {/* Template Mode */}
          {mode === 'template' && (
              <div style={styles.section}>
                <h5 style={styles.sectionTitle}>
                  <Briefcase size={20} />
                  Mall
                </h5>

                {/* Template Selection */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>Sök och välj mall *</label>
                  <Select
                    options={verificationTemplates.map(t => ({
                      value: t.template_id,
                      label: t.template_name,
                      description: t.description,
                      type: t.template_type
                    }))}
                    value={selectedTemplate ?
                      verificationTemplates.map(t => ({
                        value: t.template_id,
                        label: t.template_name,
                        description: t.description,
                        type: t.template_type
                      })).find(opt => opt.value === selectedTemplate) : null
                    }
                    onChange={handleTemplateChange}
                    placeholder="Skriv för att söka mallar..."
                    isClearable
                    isSearchable
                    noOptionsMessage={() => "Inga mallar hittades"}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        padding: '0.25rem',
                        fontSize: '14px'
                      }),
                      menuPortal: (provided) => ({
                        ...provided,
                        zIndex: 9999
                      }),
                      menu: (provided) => ({
                        ...provided,
                        fontSize: '14px'
                      })
                    }}
                  />
                </div>

                {/* Template Details Card */}
                {selectedTemplateDetails && (
                  <div style={styles.templateDetailsCard}>
                    <div style={styles.templateDetailsHeader}>
                      <div>
                        <h6 style={styles.templateDetailsTitle}>{selectedTemplateDetails.template_name}</h6>
                        {selectedTemplateDetails.description && (
                          <p style={styles.templateDetailsDescription}>{selectedTemplateDetails.description}</p>
                        )}
                      </div>
                      <Button
                        variant="link"
                        onClick={handleClearTemplate}
                        style={styles.clearTemplateButton}
                      >
                        <X size={16} style={{ marginRight: '4px' }} />
                        Byt mall
                      </Button>
                    </div>
                    {selectedTemplateDetails.template_type && (
                      <div style={styles.templateTypeBadge}>
                        {selectedTemplateDetails.template_type}
                      </div>
                    )}
                  </div>
                )}

                {/* Template Parameters */}
                {templateParameters.length > 0 && (
                  <div style={styles.section}>
                    <h5 style={styles.sectionTitle}>
                      <Hash size={20} />
                      Parametrar
                    </h5>
                    <div style={styles.parametersGrid}>
                      {templateParameters.map((param, index) => (
                        <div key={index} style={styles.formGroup}>
                          <label style={styles.label}>{param.parameter_label} *</label>
                          <div style={styles.inputWrapper}>
                            {param.parameter_type === 'Number' ? (
                              <DollarSign size={18} style={styles.inputIcon} />
                            ) : param.parameter_type === 'Date' ? (
                              <Calendar size={18} style={styles.inputIcon} />
                            ) : (
                              <FileText size={18} style={styles.inputIcon} />
                            )}
                            <Form.Control
                              type={param.parameter_type === 'Number' ? 'number' : param.parameter_type === 'Date' ? 'date' : 'text'}
                              value={parameterValues[param.parameter_name] || ''}
                              onChange={(e) =>
                                setParameterValues({
                                  ...parameterValues,
                                  [param.parameter_name]: e.target.value,
                                })
                              }
                              style={styles.input}
                              step={param.parameter_type === 'Number' ? '0.01' : undefined}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    <Button
                      variant="primary"
                      onClick={handleGeneratePreview}
                      disabled={!selectedTemplate || templateParameters.some(p => !parameterValues[p.parameter_name])}
                      style={styles.generateButton}
                    >
                      <CheckCircle size={18} style={{ marginRight: '8px' }} />
                      Generera förhandsgranskning
                    </Button>
                  </div>
                )}
              </div>
          )}

          {/* Manual Mode */}
          {mode === 'manual' && (
            <div style={styles.section}>
              <h5 style={styles.sectionTitle}>
                <Edit2 size={20} />
                Transaktioner
              </h5>
            </div>
          )}

        {/* Transactions Table (shown in both modes when there are transactions) */}
        {transactions.length > 0 && (
          <div style={styles.section}>
            <h5 style={styles.sectionTitle}>
              <FileText size={20} />
              Transaktioner
              {mode === 'template' && <span style={styles.editableHint}>(Klicka för att redigera)</span>}
            </h5>

            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Konto</th>
                    <th style={styles.th}>Debet</th>
                    <th style={styles.th}>Kredit</th>
                    <th style={styles.th}>Åtgärd</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((txn, index) => (
                    <tr key={index} style={styles.tr}>
                      <td style={styles.td}>
                        <Select
                          options={allAccounts.map(acc => ({
                            value: acc.account_id,
                            label: `${acc.account_number} - ${acc.account_name}`,
                            account_number: acc.account_number,
                            account_name: acc.account_name
                          }))}
                          value={txn.account_id ?
                            {
                              value: txn.account_id,
                              label: `${txn.account_number} - ${txn.account_name}`,
                              account_number: txn.account_number,
                              account_name: txn.account_name
                            } : null
                          }
                          onChange={(selectedOption) => handleAccountSelectChange(index, selectedOption)}
                          placeholder="Sök konto..."
                          isClearable
                          isSearchable
                          noOptionsMessage={() => "Inget konto hittades"}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: '36px',
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              fontSize: '13px'
                            }),
                            menuPortal: (provided) => ({
                              ...provided,
                              zIndex: 9999
                            }),
                            menu: (provided) => ({
                              ...provided,
                              fontSize: '13px'
                            }),
                            option: (provided) => ({
                              ...provided,
                              fontSize: '13px'
                            })
                          }}
                        />
                      </td>
                      <td style={styles.td}>
                        <Form.Control
                          type="number"
                          value={txn.debit || ''}
                          onChange={(e) => handleTransactionChange(index, 'debit', e.target.value)}
                          style={styles.tableInput}
                          step="0.01"
                          placeholder="0.00"
                        />
                      </td>
                      <td style={styles.td}>
                        <Form.Control
                          type="number"
                          value={txn.credit || ''}
                          onChange={(e) => handleTransactionChange(index, 'credit', e.target.value)}
                          style={styles.tableInput}
                          step="0.01"
                          placeholder="0.00"
                        />
                      </td>
                      <td style={styles.td}>
                        <Button
                          variant="link"
                          onClick={() => handleRemoveTransaction(index)}
                          style={{
                            ...styles.removeButton,
                            opacity: mode === 'manual' && transactions.length <= 2 ? 0.3 : 1,
                            cursor: mode === 'manual' && transactions.length <= 2 ? 'not-allowed' : 'pointer'
                          }}
                          title={mode === 'manual' && transactions.length <= 2 ? 'Minst 2 rader krävs' : 'Ta bort rad'}
                          disabled={mode === 'manual' && transactions.length <= 2}
                        >
                          <Trash2 size={18} color="#ef4444" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  <tr style={styles.totalRow}>
                    <td style={{ ...styles.td, fontWeight: '600' }}>Totalt</td>
                    <td style={{ ...styles.td, fontWeight: '600', color: '#3b82f6' }}>
                      {formatCurrency(totalDebit)}
                    </td>
                    <td style={{ ...styles.td, fontWeight: '600', color: '#3b82f6' }}>
                      {formatCurrency(totalCredit)}
                    </td>
                    <td style={styles.td}></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Balance Check */}
            <Alert
              variant={Math.abs(totalDebit - totalCredit) < 0.01 ? 'success' : 'warning'}
              style={styles.balanceAlert}
            >
              {Math.abs(totalDebit - totalCredit) < 0.01 ? (
                <>
                  <CheckCircle size={18} style={{ marginRight: '8px' }} />
                  Verifikationen är balanserad
                </>
              ) : (
                <>
                  <AlertCircle size={18} style={{ marginRight: '8px' }} />
                  Differens: {formatCurrency(Math.abs(totalDebit - totalCredit))}
                  {totalDebit > totalCredit ? ' (Debet > Kredit)' : ' (Kredit > Debet)'}
                </>
              )}
            </Alert>

            {mode === 'manual' && (
              <Button
                variant="outline-primary"
                onClick={handleAddTransaction}
                style={styles.addButton}
              >
                <Plus size={18} style={{ marginRight: '8px' }} />
                Lägg till rad
              </Button>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer style={styles.footer}>
        <Button variant="light" onClick={onHide} style={styles.cancelButton}>
          Avbryt
        </Button>
        <Button
          variant="success"
          onClick={handleConfirmVerification}
          disabled={!isVerificationValid() || isLoading}
          style={styles.confirmButton}
        >
          {isLoading ? (
            'Skapar...'
          ) : (
            <>
              <CheckCircle size={18} style={{ marginRight: '8px' }} />
              Bekräfta och skapa verifikation
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

// Modern styles
const styles = {
  header: {
    borderBottom: '1px solid #e5e7eb',
    padding: '20px 24px',
    position: 'relative',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '20px',
    fontWeight: '600',
    color: '#1f2937',
    margin: 0,
  },
  body: {
    padding: '24px',
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  footer: {
    borderTop: '1px solid #e5e7eb',
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  alert: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
    borderRadius: '8px',
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: '1px solid #f3f4f6',
  },
  editableHint: {
    fontSize: '13px',
    fontWeight: '400',
    color: '#6b7280',
    marginLeft: '8px',
    fontStyle: 'italic',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px',
    display: 'block',
  },
  inputWrapper: {
    position: 'relative',
  },
  inputIcon: {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#9ca3af',
    zIndex: 1,
  },
  input: {
    paddingLeft: '40px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    transition: 'all 0.2s ease',
    height: '42px',
  },
  parametersGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginBottom: '20px',
  },
  generateButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 20px',
    borderRadius: '8px',
    fontWeight: '500',
    fontSize: '14px',
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 20px',
    borderRadius: '8px',
    fontWeight: '500',
    fontSize: '14px',
    marginTop: '12px',
  },
  tableWrapper: {
    overflowX: 'auto',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  th: {
    padding: '12px',
    textAlign: 'left',
    backgroundColor: '#f9fafb',
    fontWeight: '600',
    color: '#374151',
    borderBottom: '2px solid #e5e7eb',
  },
  tr: {
    borderBottom: '1px solid #f3f4f6',
  },
  td: {
    padding: '8px 12px',
    verticalAlign: 'middle',
  },
  totalRow: {
    backgroundColor: '#f9fafb',
    borderTop: '2px solid #e5e7eb',
  },
  tableInput: {
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '6px 10px',
    fontSize: '13px',
    width: '100%',
  },
  removeButton: {
    padding: '4px',
    border: 'none',
    background: 'transparent',
  },
  balanceAlert: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '12px',
  },
  cancelButton: {
    padding: '10px 20px',
    borderRadius: '8px',
    fontWeight: '500',
    fontSize: '14px',
    border: '1px solid #d1d5db',
  },
  confirmButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 20px',
    borderRadius: '8px',
    fontWeight: '500',
    fontSize: '14px',
  },
  templateDetailsCard: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '16px',
    marginTop: '16px',
    marginBottom: '16px',
  },
  templateDetailsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '8px',
  },
  templateDetailsTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    margin: '0 0 4px 0',
  },
  templateDetailsDescription: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0,
  },
  clearTemplateButton: {
    padding: '4px 12px',
    fontSize: '13px',
    color: '#64748b',
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
  },
  templateTypeBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    background: '#eff7f1ff',
    color: '#44814E',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
    marginTop: '8px',
  },
};

export default VerificationModal;
